import { Link } from "react-router-dom";
import { useEffect } from "react";
import { TERMS } from "@/data/glossary";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";

// Build an auto-link index from term titles + multi-word aliases (skip short acronyms to avoid false matches).
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const LINK_TARGETS = [];
TERMS.forEach((t) => {
  [t.title, ...(t.aka || [])]
    .filter((v) => v && v.length >= 5 && v !== t.acronym)
    .forEach((v) => LINK_TARGETS.push({ text: v, slug: t.slug }));
});
LINK_TARGETS.sort((a, b) => b.text.length - a.text.length);
const LINK_LOOKUP = LINK_TARGETS.reduce((m, x) => { const k = x.text.toLowerCase(); if (!m[k]) m[k] = x.slug; return m; }, {});
const LINK_RE = LINK_TARGETS.length
  ? new RegExp(`\\b(${LINK_TARGETS.map((x) => escapeRe(x.text)).join("|")})\\b`, "gi")
  : null;

// Auto-links the first mention of each other glossary term inside body text (internal SEO linking).
export function LinkedText({ text, currentSlug }) {
  if (!text || !LINK_RE) return text;
  const used = new Set([currentSlug]);
  const out = [];
  let last = 0, m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    const slug = LINK_LOOKUP[m[0].toLowerCase()];
    if (slug && !used.has(slug)) {
      used.add(slug);
      if (m.index > last) out.push(text.slice(last, m.index));
      out.push(<Link key={m.index} to={`/terms/${slug}`} className="text-kid-teal font-medium hover:underline" data-testid={`inline-link-${slug}`}>{m[0]}</Link>);
      last = m.index + m[0].length;
    }
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function useSeo({ title, description, jsonLd, noindex }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.setAttribute("content", description);
    }
    let robots;
    if (noindex) {
      robots = document.createElement("meta");
      robots.name = "robots";
      robots.content = "noindex";
      document.head.appendChild(robots);
    }
    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => { if (script) document.head.removeChild(script); if (robots) document.head.removeChild(robots); };
  }, [title, description, jsonLd, noindex]);
}

export function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-white text-kid-navy font-poppins flex flex-col">
      <PublicNav />
      <main className="flex-1 min-w-0">{children}</main>
      <PublicFooter />
    </div>
  );
}

export function SignupCta({ variant = "band", testid = "glossary-cta" }) {
  if (variant === "inline") {
    return (
      <div className="rounded-2xl border border-kid-line bg-kid-skytint/40 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid={testid}>
        <p className="text-sm text-kid-navy flex-1">
          <b>Not sure what applies to your family?</b> KidPath helps you understand available programs, organize next steps, and navigate support with more clarity.
        </p>
        <Link to="/register" className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-kid-tealcta text-white text-sm font-semibold px-5 py-2.5 hover:-translate-y-0.5 transition-transform" data-testid={`${testid}-btn`}>
          Start your free support check →
        </Link>
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-kid-navy text-white p-8 sm:p-10 text-center" data-testid={testid}>
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold mb-2">Find out what support may be available.</h2>
      <p className="text-white/80 max-w-xl mx-auto mb-6">Take the first step toward a clearer plan for funding, services, and next steps — free, and built for families navigating autism support.</p>
      <div className="flex flex-wrap gap-3 justify-center">
        <Link to="/register" className="inline-flex items-center gap-1.5 rounded-full bg-kid-tealcta text-white text-sm font-semibold px-6 py-3 hover:-translate-y-0.5 transition-transform" data-testid={`${testid}-primary`}>
          Start your free support check →
        </Link>
        <Link to="/#how" className="inline-flex items-center rounded-full border border-white/30 text-white text-sm font-semibold px-6 py-3 hover:bg-white/10 transition-colors" data-testid={`${testid}-secondary`}>
          See how it works
        </Link>
      </div>
    </div>
  );
}
